import type { Me, SpotInProgress } from '@myrewards/contracts';
import { queryOptions, useQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { Link } from 'react-aria-components';
import { isApiError } from '../api';
import { getSpotInProgress } from '../api/spot';
import { hasRole } from '../session/presenter';
import { QUEUE_STAGE } from '../spot/stages';
import { ButtonLink, Card, cx, EmptyState, formatCount, plural } from '../ui';
import s from './Home.module.css';

/**
 * Where every Spot award is: the All Spot awards tab (APP-226), Manage › Spot awards since APP-234,
 * which opens on the ones in progress (APP-233), as this card counts them. `?stage=all` asks it
 * for every stage.
 */
const ALL_HREF = '/manage/spot/all';
const EVERY_STAGE_HREF = `${ALL_HREF}?stage=all`;

/**
 * Whether Home shows this person the card (APP-232 D3): whoever reads every row of All Spot awards,
 * the committee (`spotCommittee`, the server's own answer), the Stock Admin team and System Admins,
 * live only. A manager who is none of those has their own rows in Waiting on you instead. The
 * server checks again (404 for anyone else).
 */
export function seesSpotInProgress(me: Pick<Me, 'roles' | 'preview' | 'spotCommittee'>): boolean {
  return (
    me.preview === null &&
    (me.spotCommittee === true || hasRole(me, 'stock_admin') || hasRole(me, 'system_admin'))
  );
}

export const spotInProgressQuery = queryOptions({
  queryKey: ['spot', 'in-progress'] as const,
  queryFn: ({ signal }) => getSpotInProgress(signal),
  staleTime: 30_000,
  retry: false,
});

/**
 * APP-232 (Tom: Stock Admins "need to see Spot awards and where each one is at"; the committee
 * "the same tab"): Home's Spot awards in progress. How many colleagues nominated are at each stage
 * still in progress, in the words All Spot awards uses, each opening that tab at that stage. Counts
 * only: who, how much and who nominated are a click away, for those who may read them.
 */
export function SpotInProgressCard(): ReactElement | null {
  const read = useQuery(spotInProgressQuery);
  // Not theirs after all (the server's own check): no card, rather than an error.
  if (read.isError && isApiError(read.error) && read.error.status === 404) return null;
  return (
    <Card
      title="Spot awards in progress"
      titleSize="md"
      help={{
        shows:
          'Every colleague nominated for a Spot Award whose nomination hasn’t ended yet, counted by where it is: with the committee, with the Executives for funding, waiting on the manager, needing a supervisor, its letter, Payroll. It never shows who nominated or what they wrote.',
        canDo:
          'Open a stage to see those colleagues in All Spot awards, with who each one waits on and the place to act.',
      }}
      aside={
        <ButtonLink href={ALL_HREF} variant="ghost" size="sm">
          All Spot awards
        </ButtonLink>
      }
    >
      {read.isPending ? (
        <p role="status" className={s.meta}>
          Loading Spot awards…
        </p>
      ) : read.isError ? (
        <p className={s.meta}>
          Where each Spot award is shows on the{' '}
          <Link href={ALL_HREF} className={s.strongLink}>
            All Spot awards page
          </Link>
          .
        </p>
      ) : (
        <SpotStages data={read.data} />
      )}
    </Card>
  );
}

function SpotStages({ data }: { data: SpotInProgress }): ReactElement {
  if (data.total === 0)
    return (
      <EmptyState
        variant="first-day"
        art="award"
        size="sm"
        headline="No Spot awards in progress"
        body="When a colleague is nominated, they show here by where it is, from the committee’s review to Payroll."
        action={{ label: 'See every Spot award', href: EVERY_STAGE_HREF }}
      />
    );
  // Only the stages someone is at; each opens the tab filtered to it.
  const stages = data.stages.filter((st) => st.count > 0);
  return (
    <>
      <p className={s.spotTotal}>
        <span className={s.spotTotalLabel}>Spot awards · in progress</span>
        <span className={s.spotTotalValue}>
          {plural(data.total, 'colleague nominated', 'colleagues nominated')}
        </span>
      </p>
      <ul className={s.spotStages} aria-label="Spot awards in progress, by stage">
        {stages.map((st) => (
          <li key={st.stage}>
            <Link
              href={`${ALL_HREF}?stage=${st.stage}`}
              className={cx(s.spotStage, QUEUE_STAGE[st.stage].tone === 'problem' && s.spotProblem)}
            >
              <span className={s.spotStageName}>{QUEUE_STAGE[st.stage].label}</span>
              <span className={s.spotStageCount}>{formatCount(st.count)}</span>
            </Link>
          </li>
        ))}
      </ul>
      <p className={s.poolsFoot}>
        Scoped to Spot awards in progress: sent nominations not yet closed, not awarded or
        cancelled, one per colleague.
      </p>
    </>
  );
}
